import { Capacitor } from '@capacitor/core';
import { FileOpener } from '@capacitor-community/file-opener';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { 
  getExtensionFromUrl, 
  getMimeType, 
  isImageFile, 
  isPdfFile, 
  generateTempFileName 
} from './fileUtils';

// Platform detection
const isNative = Capacitor.isNativePlatform();


// Open file with native app with enhanced error handling
export const openFileWithNativeApp = async (url, fileName = 'document') => {
  if (!isNative) {
    // Fallback for web - open in new tab
    window.open(url, '_blank');
    return { success: true, method: 'browser' };
  }

  try {
    const ext = getExtensionFromUrl(url);
    const mime = getMimeType(url);
    const tempFileName = generateTempFileName(fileName, ext);

    // Download file to temporary cache directory
    const downloadResult = await Filesystem.downloadFile({
      path: tempFileName,
      url: url,
      directory: Directory.Cache,
    });

    if (!downloadResult.path) {
      throw new Error('Failed to download file.');
    }

    // Open the downloaded file
    await FileOpener.open({
      filePath: downloadResult.path,
      mimeType: mime,
      fileSystem: 'CACHE',
    });
    return { success: true, method: 'native' };
  } catch (error) {
    // Fallback to opening the URL in the browser
    window.open(url, '_blank');
    return { success: false, method: 'browser_fallback', error: error.message };
  }
};

// Get appropriate viewer component based on platform and file type
export const getViewerType = (url) => {
  if (!url) return 'fallback';
  if (isImageFile(url)) {
    return 'image'; // Always use image viewer
  }
  
  if (isNative) {
    return 'native'; // Use native file opener on mobile
  }
  
  if (isPdfFile(url)) {
    return 'pdf'; // Use PDF viewer on web
  }
  
  return 'fallback'; // Use fallback for other file types
};

// Platform-specific file handling
export const handleFileView = async (url, fileName, type) => {
  const viewerType = getViewerType(url);
  
  switch (viewerType) {
    case 'image':
      return 'modal'; // Show in modal
    case 'native':
      const result = await openFileWithNativeApp(url, fileName);
      return result.success ? 'opened' : 'error';
    case 'pdf':
      return 'modal'; // Show PDF in modal on web
    case 'fallback':
      const fallbackResult = await openFileWithNativeApp(url, fileName);
      return fallbackResult.success ? 'opened' : 'error';
    default:
      return 'modal';
  }
};

// Check if file is accessible
export const checkFileAccessibility = async (url) => {
  try {
    const response = await fetch(url, { method: 'HEAD' });
    return response.ok;
  } catch (error) {
    return false;
  }
}; 